import { mapRange } from 'lib/clinic/library';
import { ITranscription } from 'lib/types/utilTypes';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppActions, UtilActions } from 'store/actionCreators';
import { useShallowAppSelector } from 'store/hooks';

const SILENCE_THRESHOLD = 30;

export const useRecording = () => {
  // SECTION: declare server state
  const { audioTranscriptionData, audioTranscriptionSuccess, audioTranscriptionPending } =
    useShallowAppSelector(state => ({
      audioTranscriptionData: state.util.audioTranscription.data,
      audioTranscriptionSuccess: state.util.audioTranscription.success,
      audioTranscriptionPending: state.util.audioTranscription.pending,
    }));

  const [recordedList, setRecordedList] = useState<ITranscription[]>([]);
  const [recordedText, setRecordedText] = useState<string>('');
  const [recordedSummary, setRecordedSummary] = useState<string>('');
  const [recordedDate, setRecordedDate] = useState<number | null>(null);
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [recordingTime, setRecordingTime] = useState<number>(60);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [audioChunks, setAudioChunks] = useState<Blob[]>([]);
  const [maxDecibel, setMaxDecibel] = useState<number>(0);
  const [decibel, setDecibel] = useState<number>(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameId = useRef<number | null>(null);
  const { ipcRenderer } = window;
  const { t } = useTranslation();
  const isTranscriptionLoading = audioTranscriptionPending;
  const isInitSuccess = useMemo(() => {
    return !!mediaRecorder;
  }, [mediaRecorder]);

  // 시간을 "00:00" 형식으로 변환하는 함수
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // 오디오 녹음 초기화
  const initializeAudioRecorder = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMediaStream(stream);

      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' }); // MIME 타입 명시
      setMediaRecorder(recorder);

      audioContextRef.current = new AudioContext();
      const sourceNode = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      sourceNode.connect(analyserRef.current);
      updateDecibel();

      recorder.ondataavailable = event => {
        if (event.data.size > 0) {
          setAudioChunks(chunks => [...chunks, event.data]);
        }
      };

      recorder.onerror = error => {
        console.error('MediaRecorder error:', error); // 에러 발생 여부 확인
      };
    } catch (error) {
      const errorCode = String(error).split(':')[0];

      if (errorCode === 'NotFoundError') {
        AppActions.show_toast({
          type: 'error',
          message: t('ERROR_RECORDING_NOT_FOUND_DEVICE'),
        });
      }

      console.error('Error initializing audio recorder:', error);
    }
  };

  const updateDecibel = () => {
    if (!analyserRef.current) return;
    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    analyserRef.current.getByteFrequencyData(dataArray);
    const sum = dataArray.reduce((acc, value) => acc + value);
    const average = sum / bufferLength;
    const decibel = Number((20 * Math.log10(average)).toFixed(2));

    setDecibel(decibel);
    setMaxDecibel(prev => (prev < decibel ? decibel : prev));

    animationFrameId.current = requestAnimationFrame(updateDecibel);
  };

  // 파일 저장 함수
  const saveAudioFile = useCallback(async () => {
    if (audioChunks.length === 0) return;
    if (maxDecibel < SILENCE_THRESHOLD) {
      AppActions.show_toast({
        type: 'error',
        message: t('PROJECT_MICROPHONE_ALERT_DECIBEL'),
      });

      return;
    }

    // 현재 날짜와 시간을 파일명에 포함
    const now = new Date();
    const timestamp = now.toISOString().replace(/[:.]/g, '-').substring(0, 19);

    const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
    const formData = new FormData();
    formData.append('file', audioBlob, `recorded-audio-${timestamp}.webm`);

    UtilActions.audio_transcription_request(formData);
  }, [audioChunks]);

  const checkAuthMicrophone = async () => {
    if (!ipcRenderer) return;

    const result = await ipcRenderer.invoke('checkMicrophoneStatus');

    if (!result) {
      AppActions.show_toast({
        type: 'error',
        message: t('ERROR_RECORDING_AUTH'),
      });
    }
  };

  const startRecoding = useCallback(async () => {
    try {
      if (mediaRecorder) {
        mediaRecorder.start(1000);
        setIsRecording(true);
        setRecordingTime(60);
        setAudioChunks([]); // 새로운 녹음 시작 시 청크 초기화
      } else {
        throw new Error('Speech recognition or media recorder is not supported');
      }
    } catch (error) {
      console.error('Failed to start recording:', error);
      setIsRecording(false);
      setRecordingTime(60);
    }
  }, [mediaRecorder]);

  const stopRecording = () => {
    if (mediaStream) {
      mediaStream.getTracks().forEach(track => track.stop()); // 스트림 정지
      audioContextRef.current?.close();
      cancelAnimationFrame(animationFrameId.current!);
    }
  };

  useEffect(() => {
    if (isInitSuccess) {
      startRecoding();
    }
  }, [isInitSuccess]);

  // 타이머 효과
  useEffect(() => {
    let timerInterval: NodeJS.Timer | null = null;

    if (isRecording) {
      timerInterval = setInterval(() => {
        setRecordingTime(prev => {
          return prev > 0 ? prev - 1 : 0;
        });
      }, 1000);
    }

    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [isRecording]);

  useEffect(() => {
    if (recordingTime === 0) {
      handleStopRecording();
    }
  }, [recordingTime]);

  useEffect(() => {
    if (audioTranscriptionSuccess && !!audioTranscriptionData) {
      // setRecordedSummary(audioTranscriptionData.summary);
      console.log('HDS] audioTranscriptionData _______ ', audioTranscriptionData);
      const splitData = audioTranscriptionData.text?.replace('\n\n', '\n');
      console.log('HDS] splitData _______ ', splitData);
      setRecordedText(splitData);
      setRecordedSummary(audioTranscriptionData.summary);
      setRecordedDate(audioTranscriptionData.recordDate);
      setRecordedList(draft => [
        ...draft,
        {
          text: splitData,
          summary: audioTranscriptionData.summary,
          recordDate: audioTranscriptionData.recordDate,
        },
      ]);
    }
  }, [audioTranscriptionSuccess, audioTranscriptionData]);

  const initRecording = () => {
    setRecordedText('');
    setIsRecording(false);
    setRecordingTime(60);
    setMediaRecorder(null);
    setMediaStream(null);
    setAudioChunks([]);
    setMaxDecibel(0);
    audioContextRef.current = null;
    analyserRef.current = null;
    animationFrameId.current = null;
  };

  const handleStartRecording = () => {
    initRecording();
    checkAuthMicrophone();
    initializeAudioRecorder();
  };

  const handleStopRecording = useCallback(() => {
    if (mediaRecorder) {
      mediaRecorder.stop();
      stopRecording();
      initRecording();
      // 녹음 중지 후 파일 자동 저장
      setTimeout(() => saveAudioFile(), 100); // 약간의 지연을 주어 마지막 청크가 추가되도록 함
    }
  }, [mediaRecorder, saveAudioFile]);

  return {
    recordedList: recordedList,
    recordedText: recordedText,
    recordedSummary: recordedSummary,
    recordedDate: recordedDate,
    isRecording,
    isTranscriptionLoading,
    recordingTime: formatTime(recordingTime),
    timeProgress: mapRange(recordingTime, 0, 60, 0, 100),
    decibel,
    onStartRecording: handleStartRecording,
    onStopRecording: handleStopRecording,
  };
};
